import React, { useState } from 'react';
import { Filter, Download, Search, Calendar, ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency, formatNumber, formatDate, formatPercentage, formatIDR, formatCryptoIDR, USD_TO_IDR } from '../utils/formatters';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';

const History = () => {
  const [transactions] = useState([
    {
      id: 1,
      date: new Date().toISOString(),
      type: 'arbitrage',
      coin: 'BTC',
      buyExchange: 'MEXC',
      sellExchange: 'Binance',
      amount: 0.025,
      buyPrice: 75388,
      sellPrice: 76920,
      profit: 38.30,
      profitPercentage: 2.03,
      status: 'completed',
      txHash: '0x1234...abcd',
    },
    {
      id: 2,
      date: new Date(Date.now() - 3600000).toISOString(),
      type: 'manual',
      coin: 'ETH',
      exchange: 'Binance',
      action: 'buy',
      amount: 2.5,
      price: 3800,
      total: 9500,
      status: 'completed',
      txHash: '0x5678...efgh',
    },
    {
      id: 3,
      date: new Date(Date.now() - 7200000).toISOString(),
      type: 'arbitrage',
      coin: 'BNB',
      buyExchange: 'Gate.io',
      sellExchange: 'MEXC',
      amount: 10,
      buyPrice: 650,
      sellPrice: 658.5,
      profit: 85,
      profitPercentage: 1.31,
      status: 'completed',
      txHash: '0x9012...ijkl',
    },
    {
      id: 4,
      date: new Date(Date.now() - 10800000).toISOString(),
      type: 'manual',
      coin: 'SOL',
      exchange: 'MEXC',
      action: 'sell',
      amount: 50,
      price: 182.25,
      total: 9112.50,
      status: 'completed',
      txHash: '0x3456...mnop',
    },
    {
      id: 5,
      date: new Date(Date.now() - 86400000).toISOString(),
      type: 'arbitrage',
      coin: 'MATIC',
      buyExchange: 'Indodax',
      sellExchange: 'Binance',
      amount: 1000,
      buyPrice: 0.85,
      sellPrice: 0.87,
      profit: 20,
      profitPercentage: 2.35,
      status: 'completed',
      txHash: '0x7890...qrst',
    },
  ]);

  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    coin: 'all',
    dateFrom: '',
    dateTo: '',
    status: 'all',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const coins = ['all', 'BTC', 'ETH', 'BNB', 'SOL', 'MATIC', 'ADA', 'DOT'];
  const types = ['all', 'arbitrage', 'manual'];
  const statuses = ['all', 'completed', 'pending', 'failed'];

  const filteredTransactions = transactions.filter(tx => {
    if (filters.search && !tx.coin.toLowerCase().includes(filters.search.toLowerCase()) &&
        !tx.txHash.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.type !== 'all' && tx.type !== filters.type) return false;
    if (filters.coin !== 'all' && tx.coin !== filters.coin) return false;
    if (filters.status !== 'all' && tx.status !== filters.status) return false;
    // Add date filtering logic here if needed
    return true;
  });

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const stats = {
    totalTrades: transactions.length,
    totalProfit: transactions
      .filter(tx => tx.type === 'arbitrage')
      .reduce((sum, tx) => sum + (tx.profit || 0), 0),
    avgProfit: transactions
      .filter(tx => tx.type === 'arbitrage' && tx.profitPercentage)
      .reduce((sum, tx, _, arr) => sum + tx.profitPercentage / arr.length, 0),
    successRate: (transactions.filter(tx => tx.status === 'completed').length / transactions.length) * 100,
  };

  const exportToCSV = () => {
    // Simulate CSV export
    toast.success('Transaction history exported successfully!');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Transaction History</h1>
          <p className="text-gray-400">View and export your trading history</p>
        </div>
        <button onClick={exportToCSV} className="btn-primary flex items-center">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Total Trades</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.totalTrades}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Total Profit</p>
          <p className="text-2xl font-bold text-green-500 mt-1">
            {formatIDR(stats.totalProfit * USD_TO_IDR)}
          </p>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Avg Profit %</p>
          <p className="text-2xl font-bold text-white mt-1">
            {formatPercentage(stats.avgProfit)}
          </p>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Success Rate</p>
          <p className="text-2xl font-bold text-white mt-1">
            {stats.successRate.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </h3>
          <button
            onClick={() => setFilters({
              search: '',
              type: 'all',
              coin: 'all',
              dateFrom: '',
              dateTo: '',
              status: 'all',
            })}
            className="text-sm text-blue-500 hover:text-blue-400"
          >
            Reset Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="input-field pl-10"
                placeholder="Search by coin or tx hash"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="input-field"
            >
              {types.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Coin</label>
            <select
              value={filters.coin}
              onChange={(e) => setFilters({ ...filters, coin: e.target.value })}
              className="input-field"
            >
              {coins.map(coin => (
                <option key={coin} value={coin}>
                  {coin === 'all' ? 'All Coins' : coin}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="input-field"
            >
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
            <button className="input-field w-full text-left flex items-center justify-between">
              <span className="text-gray-400">Select dates</span>
              <Calendar className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="glass-card p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Coin</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Exchange(s)</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Price/Total</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Profit</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">TX Hash</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-border">
              {paginatedTransactions.map((tx) => (
                <tr key={tx.id} className="table-row-hover">
                  <td className="py-3 px-4 text-sm text-gray-300">{formatDate(tx.date)}</td>
                  <td className="py-3 px-4">
                    <span className={cn(
                      'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                      tx.type === 'arbitrage'
                        ? 'bg-purple-500/10 text-purple-500'
                        : 'bg-blue-500/10 text-blue-500'
                    )}>
                      {tx.type === 'arbitrage' ? 'Arbitrage' : tx.action?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white font-medium">{tx.coin}</td>
                  <td className="py-3 px-4 text-gray-300">
                    {tx.type === 'arbitrage' ? (
                      <span className="flex items-center">
                        {tx.buyExchange}
                        <TrendingUp className="h-3 w-3 mx-1 text-green-500" />
                        {tx.sellExchange}
                      </span>
                    ) : (
                      tx.exchange
                    )}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-300">
                    {formatNumber(tx.amount, 4)}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-300">
                    {tx.type === 'arbitrage' ? (
                      <div className="text-xs">
                        <p>B: {formatCryptoIDR(tx.buyPrice, 0)}</p>
                        <p>S: {formatCryptoIDR(tx.sellPrice, 0)}</p>
                      </div>
                    ) : (
                      <div>
                        <p>{formatCryptoIDR(tx.price, 0)}</p>
                        <p className="text-xs text-gray-500">
                          Total: {formatIDR(tx.total * USD_TO_IDR)}
                        </p>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {tx.type === 'arbitrage' ? (
                      <div>
                        <p className="text-green-500 font-medium">
                          +{formatIDR(tx.profit * USD_TO_IDR)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatPercentage(tx.profitPercentage)}
                        </p>
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={cn(
                      'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                      tx.status === 'completed'
                        ? 'bg-green-500/10 text-green-500'
                        : tx.status === 'pending'
                        ? 'bg-yellow-500/10 text-yellow-500'
                        : 'bg-red-500/10 text-red-500'
                    )}>
                      {tx.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <a
                      href="#"
                      className="text-blue-500 hover:text-blue-400 text-xs font-mono"
                      onClick={(e) => {
                        e.preventDefault();
                        navigator.clipboard.writeText(tx.txHash);
                        toast.success('Transaction hash copied!');
                      }}
                    >
                      {tx.txHash}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-dark-border">
            <p className="text-sm text-gray-400">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of{' '}
              {filteredTransactions.length} transactions
            </p>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-md hover:bg-dark-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <div className="flex items-center space-x-1">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={cn(
                      'w-8 h-8 rounded-md text-sm font-medium transition-colors',
                      currentPage === i + 1
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:bg-dark-hover hover:text-white'
                    )}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md hover:bg-dark-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;